import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { adminUpdateUserApi } from "api/administration";

import { allUsersGroupRolesApi } from "api/user";
import { t } from "i18next";
import { CustomError } from "interface";
import { IUserRole, UserProfile } from "interface/user";
import React, { FC, useEffect, useState } from "react";
import { useInfoStore } from "store/error";
import CustomDropDownList from "ui/dropDown";
import { CustomInput } from "ui/Input";
import { COLOR } from "utils/constant";
import { NOTIFICATION_METHOD_DROPDOWN } from "utils/dropdown/notification";

export const AdminUserInformation: FC<{
	user: UserProfile;
	setUser: (user: UserProfile) => void;
	countryCodes: string[];
	setCountryCode: (countryCodes: string) => void;
	adminPage?: boolean;
	setAllItemsSaved: (value: boolean) => void;
}> = ({
	user,
	setUser,
	countryCodes,
	setCountryCode,
	adminPage = false,
	setAllItemsSaved,
}) => {
	const [phoneNumber, setPhoneNumber] = React.useState({
		number: user.mobileNumber || "",
		prefix: user.mobilePrefix || "+47",
	});
	const [updateRequired, setUpdateRequired] = useState(false);

	const [phoneNumberError, setPhoneNumberError] = React.useState("");
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);

	const { setError } = useInfoStore();
	const [userGroups, setUserGroups] = useState<IUserRole[]>([]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		allUsersGroupRolesApi()
			.then((res) => {
				setUserGroups(res.data);
			})
			.catch((error) => {
				const err = new CustomError(
					t("UserGroupFetchFailed", "UserGroupFetchFailed"),
				);
				const showCustomError = true;
				setError(err, showCustomError);
			});
	}, [setError]);

	const handlePhoneNumberChange = (value: string): void => {
		if (
			(value === "" || /^[0-9]*$/.test(value)) &&
			(phoneNumber.prefix === "+47" ? value.length <= 8 : value.length <= 15)
		) {
			setPhoneNumber({ number: value, prefix: phoneNumber.prefix });
			setUpdateRequired(true);
		}
	};

	const saveUser = (updatedUser?: UserProfile) => {
		const userToSave = updatedUser || user;
		setAllItemsSaved(false);
		adminUpdateUserApi({
			...userToSave,
		})
			.then(() => {
				setAllItemsSaved(true);
			})
			.catch((error) => {
				console.log("error ", error);
				// showError(`${t("Failed")}  ${t("To")} ${t("Save")}`, {
				// 	duration: 3000,
				// });
			})
			.finally(() => {});
	};

	const handleBlurSave = () => {
		if (updateRequired) {

			saveUser({
				...user,
				mobileNumber: phoneNumber.number,
				mobilePrefix: phoneNumber.prefix,
			});
			setUpdateRequired(false);
		}
	};

	return (
		<div
			className="user-information-form"
			style={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: "30px",
			}}
		>
			<div
				className="user-information-form-row"
				style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("Email")}
					disabled={adminPage ? false : true}
					value={user.email || ""}
					onChange={(value) => {
						if (user.email !== value.trim()) {
							setUpdateRequired(true);
						}
						setUser({ ...user, email: value });
					}}
					onBlur={handleBlurSave}
				/>
				<MultiSelect
					style={{ width: "100%", borderColor: COLOR.Green }}
					data={userGroups}
					textField="name"
					dataItemKey="id"
					label={t("UserGroup")}
					disabled={adminPage ? false : true}
					placeholder={t("Select") + "  " + t("UserGroup")}
					onChange={(e) => {
						const selectedGroups = e.value as IUserRole[];
						setUser({
							...user,
							userGroup: selectedGroups,
						});
						saveUser({ ...user, userGroup: selectedGroups });
					}}
					// onClose={handleBlurSave}
					value={user?.userGroup || []}
				/>
			</div>
			<div
				className="user-information-form-row"
			style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("FirstName")}
					value={user.firstName || ""}
					disabled={adminPage ? false : true}
					onChange={(value) => {
						if (user.firstName !== value.trim()) {
							setUpdateRequired(true);
						}
						setUser({ ...user, firstName: value });
					}}
					onBlur={handleBlurSave}
				/>
				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("LastName")}
					value={user.lastName || ""}
					disabled={adminPage ? false : true}
					onChange={(value) => {
						if (user.lastName !== value.trim()) {
							setUpdateRequired(true);
						}
						setUser({ ...user, lastName: value });
					}}
					onBlur={handleBlurSave}
				/>
			</div>
			<div
				className="user-information-form-row"
				style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<div className="user-information-form-block" style={{ width: "100%" }}>
					<span style={{ color: COLOR.StatusRed }}>{phoneNumberError}</span>

					<div
						className="user-information-phone-row"
						style={{
							display: "flex",
							flexDirection: isMobileView ? "column" : "row",
							gap: "20px",
							width: "100%",
						}}
					>
						<CustomDropDownList
							style={{ width: isMobileView ? "100%" : "50%" }}
							data={countryCodes}
							dataItemKey=""
							textField=""
							label={t("CountryCode")}
							value={phoneNumber.prefix}
							onChange={(value) => {
								//console.log("value", value);
								const newPrefix = value.value;
								let newNumber = phoneNumber.number;

								// If prefix is +47 and number is longer than 8, cut it to 8
								if (newPrefix === "+47" && newNumber.length > 8) {
									newNumber = newNumber.slice(0, 8);
								}
								setPhoneNumber({ number: newNumber, prefix: value.value });
								saveUser({ ...user, mobilePrefix: newPrefix, mobileNumber: newNumber });

								//	setPhoneNumber({ ...phoneNumber, prefix: value.value });
								//	saveUser({ ...user, mobilePrefix: value.value });
							}}
							// onClose={handleBlurSave}
						/>

						<CustomInput
							// inputStyle={{ width: "100%" }}
							label={t("MobileNumber")}
							placeholder={t("MobileNumber")}
							value={phoneNumber.number || ""}
							isError={phoneNumberError !== ""}
							onChange={handlePhoneNumberChange}
							onBlur={handleBlurSave}
						/>
					</div>
				</div>

				<CustomInput
					inputStyle={{ width: "100%" }}
					label={t("OrganizationNumber")}
					placeholder={t("OrganizationNumber")}
					value={user.organizationNumber || ""}
					// disabled={twoFaCodeSent}
					onChange={(value) => {
						if (user.organizationNumber !== value.trim()) {
							setUpdateRequired(true);
						}
						setUser({ ...user, organizationNumber: value });
					}}
					onBlur={handleBlurSave}
				/>
			</div>

			<div
				className="user-information-form-row"
				style={{
					width: "100%",
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<div
					className="user-information-form-block"
					style={{
						gap: "20px",
						width: "100%",
						display: "flex",
						// justifyContent: "space-between",
					}}
				>
					<CustomDropDownList
						style={{ width: "100%" }}
						data={NOTIFICATION_METHOD_DROPDOWN}
						dataItemKey="id"
						textField="name"
						label={t("TwoFactorSendMedium")}
						value={user.twoFactorSendType}
						onChange={(value) => {
							const data = {
								...user,
								twoFactorSendType: {
									id: value.value.id,
									name: value.value.name,
									nameEnglish: "",
								},
							};
							setUser(data);
							saveUser(data);
						}}
						// onClose={handleBlurSave}
					/>
				</div>
				<div
					className="user-information-form-block user-information-checkbox-group"
					style={{
						width: "100%",
						display: "flex",
					}}
				>
					<div>
						<div className="">{t("SalesConfirmation")}</div>
						<div style={{ display: "flex", gap: "20px" }}>
							<Checkbox
								value={user.salesConfirmationMail}
								label={t("Email")}
								onChange={(value) => {
									const updatedUser = {
										...user,
										salesConfirmationMail: !user.salesConfirmationMail,
									};
									setUser(updatedUser);
									saveUser(updatedUser);
								}}
							/>
							<Checkbox
								value={user.salesConfirmationSms}
								label={t("SMS")}
								onChange={(value) => {
									const updatedUser = {
										...user,
										salesConfirmationSms: !user.salesConfirmationSms,
									};
									setUser(updatedUser);
									saveUser(updatedUser);
								}}
							/>
						</div>
					</div>
				</div>
			</div>
			<div
				className="user-information-form-row"
				style={{
					width: "100%",
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
					justifyContent: "space-between",
				}}
			>
				<div className="user-information-checkbox-group" style={{ width: isMobileView ? "100%" : "50%" }}>
					<div className="">{t("Invoice") + "/" + t("Settlements")}</div>
					<div style={{ display: "flex", gap: "20px" }}>
						<Checkbox
							value={user.invoiceMail}
							label={t("Email")}
							onChange={(value) => {
								const updatedUser = { ...user, invoiceMail: !user.invoiceMail };
								setUser(updatedUser);
								saveUser(updatedUser);
							}}
						/>
						<Checkbox
							value={user.invoiceSms}
							label={t("SMS")}
							onChange={(value) => {
								const updatedUser = { ...user, invoiceSms: !user.invoiceSms };
								setUser(updatedUser);
								saveUser(updatedUser);
							}}
						/>
					</div>
				</div>
				<div className="user-information-checkbox-group" style={{ width: isMobileView ? "100%" : "50%" }}>
					<div className="">{t("SalesContract")}</div>
					<div
						style={{
							display: "flex",
							gap: "20px",
							width: "100%",
							justifyContent: "flex-start",
						}}
					>
						<Checkbox
							value={user.salesContractMail}
							label={t("Email")}
							onChange={(value) => {
								const updatedUser = {
									...user,
									salesContractMail: !user.salesContractMail,
								};
								setUser(updatedUser);
								saveUser(updatedUser);
							}}
						/>
						<Checkbox
							value={user.salesContractSms}
							label={t("SMS")}
							onChange={(value) => {
								const updatedUser = {
									...user,
									salesContractSms: !user.salesContractSms,
								};
								setUser(updatedUser);
								saveUser(updatedUser);
							}}
						/>
					</div>
				</div>
			</div>
			{/* 
			<div style={{ width: "100%", display: "flex" }}>
				<div style={{ width: "100%", marginTop: "20px" }}>
					<div
						style={{
							marginTop: "20px",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<CustomButton
							btnStyle={{
								width: "70%",
								marginTop: "20px",
								maxWidth: "400px",
							}}
							label={t("Save")}
							bgColor={COLOR.Green}
							labelStyle={{ color: "white" }}
							onClick={saveUser}
						/>
					</div>
				</div>
				<div style={{ width: "100%" }}></div>
			</div> */}
		</div>
	);
};

const Checkbox: React.FC<{
	value: boolean;
	label: string;
	onChange?: (value: boolean) => void;
}> = ({ value, label, onChange }) => {
	return (
		<label style={{ display: "flex", alignItems: "center" }}>
			<input
				type="checkbox"
				checked={value}
				onChange={(e) => onChange && onChange(e.target.checked)}
				style={{
					width: "20px",
					height: "20px",
					marginRight: "10px",
				}}
			/>
			{label}
		</label>
	);
};
